import {navigateToPage} from '../../../../shared/scripts/ui_utils/ClientUtils'
import {CA} from '../../../../shared/scripts/analytics/Analytics'
import {ClientUserState} from '../../../../shared/scripts/ClientUserState'
import {setElementInnerHTML, setElementOnClick} from '../../../../shared/scripts/ui_utils/DomUtils'
import {audioManager, AudioType} from '../../../../shared/scripts/Sound'
import {AjaxDataTypes} from '../../../../../shared/AjaxDataTypes'
import {AnalyticEventCategory, AnalyticEventType, AnalyticEventValue} from '../../../../../shared/entity_datas/EntityDatas'
import {UINotificationPanelController} from '../../../../shared/scripts/notifications/UINotificationPanelController'
import {removeURLParams} from '../../../../shared/scripts/ui_utils/Utils'
import {UIPopupRulebook} from '../../../../shared/scripts/ui_popup/UIPopupRulebook'
import {RulebookPage} from '../../../../../shared/enums/RulebookEnums'
import {UIPopupController} from '../../../../shared/scripts/ui_popup/UIPopupController'
import {UIAjaxWrapper} from '../../../../shared/scripts/network/UIAjaxWrapper'
import {UIAPIEndpoints} from '../../../../shared/scripts/network/UIAPIEndpoints'
import {replaceLanguageInPath} from '../../../../shared/scripts/ClientI18n'
import {UIUtilsEjsVariables} from '../../../../shared/scripts/UIUtilsEjsVariables'

type MenuState = 'closed' | 'open'

export class UIMinimalHeaderController {
    protected readonly minimalHeader: HTMLDivElement
    protected readonly userCoinsDiv: HTMLHeadingElement
    readonly headerCoinsContainer: HTMLDivElement
    readonly notificationPanelController: UINotificationPanelController

    private readonly hamburger: HTMLDivElement
    private readonly overlayScreen: HTMLDivElement
    private readonly menu: HTMLDivElement
    private readonly closeButton: HTMLDivElement
    private readonly rulebookButton: HTMLLinkElement
    private menuState: MenuState = 'closed'

    // Mobile/tablet combined sidebar + menu
    private readonly hamburgerLeft: HTMLDivElement
    private readonly sidebarOverlay: HTMLDivElement
    private readonly combinedOverlay: HTMLDivElement
    private combinedState: MenuState = 'closed'

    private readonly logoButton: HTMLDivElement
    private logoClickCallback?: () => void

    constructor() {
        this.minimalHeader = document.getElementById('new-minimal-header') as HTMLDivElement
        this.userCoinsDiv = document.getElementById('new_minimal_header_coins') as HTMLHeadingElement
        this.headerCoinsContainer = document.getElementById('new_minimal_header_coins_container') as HTMLDivElement

        const allMenus = document.querySelectorAll('.m-mainpage-menu')
        this.menu = Array.from(allMenus).find((menu) => !menu.closest('.new-combined-overlay')) as HTMLDivElement
        this.hamburger = document.getElementById('header-element-hamburger-container') as HTMLDivElement
        this.closeButton = this.menu.querySelector('#m-mainpage-menu-close') as HTMLDivElement

        const allOverlays = document.querySelectorAll('.m-mainpage-overlay')
        this.overlayScreen = Array.from(allOverlays).find((overlay) => !overlay.closest('.new-combined-overlay')) as HTMLDivElement

        this.rulebookButton = this.menu.querySelector('#m-mainpage-menu-link-rulebook') as HTMLLinkElement

        this.hamburgerLeft = document.getElementById('header-element-hamburger-left') as HTMLDivElement
        this.sidebarOverlay = document.getElementById('new-sidebar-overlay') as HTMLDivElement
        this.combinedOverlay = document.getElementById('new-combined-overlay') as HTMLDivElement

        this.logoButton = document.getElementById('new-minimal-header-logo') as HTMLDivElement

        this.notificationPanelController = new UINotificationPanelController()
        void this.addFriendRequestsToNotificationPanel()

        ClientUserState.registerChangeListener((userState) => {
            if(userState == undefined) return
            this.setCoins(userState.items.colonistCoins)
        })

        this.initLogoClick()
        this.initCoinClick()
        this.initRightMenu()
        this.initLeftMenu()

        UIMinimalHeaderController.displayAccountDeletionPopup()
    }

    hide() {
        this.minimalHeader.classList.add('hidden')
    }

    show() {
        this.minimalHeader.classList.remove('hidden')
    }

    private initLogoClick() {
        setElementOnClick(this.logoButton, () => {
            if(this.logoClickCallback) {
                this.logoClickCallback()
            }
        })
    }

    setLogoClickCallback(callback: () => void) {
        this.logoClickCallback = callback
    }

    private setCoins(coinAmount: number) {
        setElementInnerHTML(this.userCoinsDiv, {key: 'strings:colonistCommon.utils.#buffer', options: {value: coinAmount}})
    }

    private initCoinClick() {
        setElementOnClick(this.headerCoinsContainer, () => {
            audioManager.play(AudioType.Click)
            UIMinimalHeaderController.sendCoinClickAnalytic()
            const storePath = replaceLanguageInPath('/store', UIUtilsEjsVariables.language)
            navigateToPage(storePath)
        })
    }

    private static sendCoinClickAnalytic() {
        const eventData: AjaxDataTypes.AnalyticData = {
            type: AnalyticEventType.ABTestClickAnalytics,
            category: AnalyticEventCategory.ABTestClickAnalytics,
            floatValue: AnalyticEventValue.HeaderCoinPlusButton,
        }
        void CA.sendAnalyticEvent(eventData)
    }

    private initRightMenu() {
        setElementOnClick(this.hamburger, () => this.openMenu())
        setElementOnClick(this.closeButton, () => this.closeMenu())
        setElementOnClick(this.overlayScreen, () => this.closeMenu())
        setElementOnClick(this.rulebookButton, () => {
            audioManager.play(AudioType.Click)
            new UIPopupRulebook(RulebookPage.Classic4P).show()
        })
    }

    private openMenu() {
        if(this.menuState == 'open') return
        this.menu.classList.add('open')
        this.overlayScreen.classList.remove('hidden')
        this.menuState = 'open'
    }

    closeMenu() {
        if(this.menuState == 'closed') return
        this.menu.classList.remove('open')
        this.overlayScreen.classList.add('hidden')
        this.menuState = 'closed'
    }

    private initLeftMenu() {
        setElementOnClick(this.hamburgerLeft, () => this.openCombinedOverlay())
        setElementOnClick(this.sidebarOverlay, () => this.closeCombinedOverlay())

        const combinedMenuCloseButton = this.combinedOverlay.querySelector('#m-mainpage-menu-close') as HTMLDivElement
        setElementOnClick(combinedMenuCloseButton, () => this.closeCombinedOverlay())

        const combinedMenuRulebookButton = this.combinedOverlay.querySelector('#m-mainpage-menu-link-rulebook') as HTMLLinkElement
        setElementOnClick(combinedMenuRulebookButton, () => {
            audioManager.play(AudioType.Click)
            new UIPopupRulebook(RulebookPage.Classic4P).show()
        })
    }

    private openCombinedOverlay() {
        if(this.combinedState == 'open') return
        this.combinedOverlay.classList.add('open')
        this.sidebarOverlay.classList.remove('hidden')
        this.combinedState = 'open'
    }

    closeCombinedOverlay() {
        if(this.combinedState == 'closed') return
        this.combinedOverlay.classList.remove('open')
        this.sidebarOverlay.classList.add('hidden')
        this.combinedState = 'closed'
    }

    private async addFriendRequestsToNotificationPanel() {
        if(ClientUserState.userState == undefined) return

        const friendRequestsResponse = await UIAjaxWrapper.getRequestHandler(UIAPIEndpoints.apiFriendRequestsReceived(), undefined)
        if(friendRequestsResponse == undefined) return
        const friendRequests = friendRequestsResponse.json as AjaxDataTypes.FriendRequestReceivedData[]
        const friendRequestsNumber: number = friendRequests !== undefined ? friendRequests.length : 0
        if(friendRequestsNumber === 0) return

        if(this.notificationPanelController == undefined) return

        for(const friendRequestReceived of friendRequests) {
            this.notificationPanelController.addFriendRequestNotification(friendRequestReceived.username, new Date(friendRequestReceived.dateCreated))
        }
    }

    private static displayAccountDeletionPopup() {
        const urlSearchParams = new URLSearchParams(window.location.search)
        if(!urlSearchParams.has('account-deleted')) return

        UIPopupController.createSimplePopup({key: 'strings:popups.accountDeletionConfirmation.title'}, {key: 'strings:popups.accountDeletionConfirmation.body'})
        removeURLParams()
    }
}
